import { h, Host, forceUpdate } from "@stencil/core";
import { i18nProvider } from "@trumpf/ux.i18n";
import loadTranslations from "../../utils/loadTranslations";
import { createAuthStore, SilentAuth } from "../../utils/auth";
import { getEnvConfig, setEnv } from "../../api/config";
import { Documents } from "../../api/publicApi/serviceReports/documents";
import { GroupedReports } from "./grouped-reports";
import { Filter } from "./filter";
export class ServiceReports {
    constructor() {
        /**
         * The locale to use for translations and the phone input
         */
        this.locale = "en-US";
        /**
         * use onprem Public API instead of azure
         * @deprecated only used until April 2025
         */
        this.legacyApi = false;
        /**
         * don't show the filter.
         * Regardless the filter will always automatically be hidden when filtering does not make sense (e.g. only one document).
         */
        this.hideFilter = false;
        /**
         * hide new report types
         * @deprecated will be removed in the future
         **/
        this.hideNewReportTypes = false;
        this.authStore = createAuthStore();
        this.handleFilterChange = (filters) => {
            this.documents.store.state.filters = filters;
        };
    }
    componentWillLoad() {
        setEnv(`${this.env}${this.legacyApi ? "Legacy" : ""}`);
        this.config = getEnvConfig();
        this.documents = new Documents(Object.assign(Object.assign({}, this.config), { hideNewReportTypes: this.hideNewReportTypes }), () => this.onTokenReady());
    }
    connectedCallback() {
        this.i18nSubscriberId = i18nProvider.subscribe(() => forceUpdate(this));
        this.loadTranslations();
    }
    onLocaleChange() {
        if (this.locale !== i18nProvider.language) {
            this.loadTranslations();
        }
    }
    loadTranslations() {
        loadTranslations(this.locale).then((locale) => {
            i18nProvider.changeLanguage(locale);
            this.locale = locale;
        });
    }
    disconnectedCallback() {
        const { documents } = this.documents.store.state;
        documents === null || documents === void 0 ? void 0 : documents.forEach((doc) => doc.destroy());
        i18nProvider.unsubscribe(this.i18nSubscriberId);
    }
    async onTokenReady() {
        if (this.missionNumber) {
            await this.documents.getDocumentsForMission(this.missionNumber);
        }
        else if (this.machineNumber) {
            await this.documents.getDocumentsForMachine(this.machineNumber);
        }
    }
    render() {
        const { isLoading, error, documents, maxDate, minDate } = this.documents.store.state;
        const { t } = i18nProvider;
        const isOnMachinePage = !!this.machineNumber && !this.missionNumber;
        return (h(Host, { key: '742e1690899bafb5466340010653f6605d0a022e', class: "mf-service-reports" }, h("h2", { key: '87fc5324ab1bf13e17d706690f291180d9d2c90a', class: "ux-text-headline ux-text-headline--big ux-u-text-align--center" }, t("mf-service-calls:serviceReports.headline")), isOnMachinePage && h("p", { key: '7eb756b87e4b408e27c6adea3f5f4ab43e6ac06e', class: "ux-text-normal" }, t("mf-service-calls:serviceReports.sublineMachine")), !this.hideFilter && (h(Filter, { key: '5c087ec7170e210dcedbe8f946f4755f1685aa7e', handleFilterChange: this.handleFilterChange, t: t, minDate: minDate, maxDate: maxDate, documents: documents, hideNewReportTypes: this.hideNewReportTypes })), h(GroupedReports, { key: 'f84ee9d9e0a484f66855bf22c160851153340611', t: t, documents: documents, filteredDocuments: this.documents.getFilteredDocuments(documents), isOnMachinePage: isOnMachinePage, machineNumber: this.machineNumber, machineAlias: this.machineAlias, machineName: this.machineName, technicianName: this.technicianName }), isLoading && h("ux-loader", { key: '57b663a97955cbe1c7c129da76efd5104c03e733', variant: "spinner" }), error && (h("div", { key: 'a53945519e1ec7dc14fd76a50f73a1c397c1fec5', class: "mf-service-reports__error ux-text-normal" }, t("mf-service-calls:serviceReports.loadError"), ": ", error)), h(SilentAuth, { key: '41aa76808609077ff7e47b2a1f0bb2dc361210bf', disabled: false, config: this.config, authStore: this.authStore })));
    }
    static get is() { return "mf-service-reports"; }
    static get originalStyleUrls() {
        return {
            "$": ["service-reports.css"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["service-reports.css"]
        };
    }
    static get properties() {
        return {
            "machineNumber": {
                "type": "string",
                "attribute": "machine-number",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "identifier of Machine - one of machineNumber OR missionNumber are required"
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "missionNumber": {
                "type": "string",
                "attribute": "mission-number",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "identifier of Mission - one of machineNumber OR missionNumber are required"
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "locale": {
                "type": "string",
                "attribute": "locale",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The locale to use for translations and the phone input"
                },
                "getter": false,
                "setter": false,
                "reflect": true,
                "defaultValue": "\"en-US\""
            },
            "env": {
                "type": "string",
                "attribute": "env",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "environment"
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "legacyApi": {
                "type": "boolean",
                "attribute": "legacy-api",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "deprecated",
                            "text": "only used until April 2025"
                        }],
                    "text": "use onprem Public API instead of azure"
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "false"
            },
            "technicianName": {
                "type": "string",
                "attribute": "technician-name",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "technician name to be used in the reports"
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "machineAlias": {
                "type": "string",
                "attribute": "machine-alias",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "machine alias to be used in the reports"
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "machineName": {
                "type": "string",
                "attribute": "machine-name",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "machine name to be used in the reports"
                },
                "getter": false,
                "setter": false,
                "reflect": false
            },
            "hideFilter": {
                "type": "boolean",
                "attribute": "hide-filter",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "don't show the filter.\nRegardless the filter will always automatically be hidden when filtering does not make sense (e.g. only one document)."
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "false"
            },
            "hideNewReportTypes": {
                "type": "boolean",
                "attribute": "hide-new-report-types",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [{
                            "name": "deprecated",
                            "text": "will be removed in the future"
                        }],
                    "text": "hide new report types"
                },
                "getter": false,
                "setter": false,
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get watchers() {
        return [{
                "propName": "locale",
                "methodName": "onLocaleChange"
            }];
    }
}
//# sourceMappingURL=service-reports.js.map
